import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { PageHeader, SegmentTabs, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "documents.company_license.files" -> "Documents company license files"
const humanize = (req) => {
  const text = String(req).replace(/[._]+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// The API stores "Paypal"; older rows may say "paypal".
const isPaypal = (preference) => String(preference || "").toLowerCase() === "paypal";

const StripeLogo = () => <span className="lwp-stripe-word">stripe</span>;

const StripePanel = ({ preference, onUseStripe, savingPref }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await lawyerService.getStripeStatus();
      setStatus(res?.data?.data || null);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Unable to load Stripe status."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Onboarding happens in another tab; re-check when the lawyer comes back.
    const onFocus = () => document.visibilityState === "visible" && loadStatus();
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [loadStatus]);

  const openOnboarding = async () => {
    // Open the tab synchronously so pop-up blockers allow it, then point it at Stripe.
    const tab = window.open("", "_blank");
    setLinking(true);
    try {
      const res = await lawyerService.getStripeConnectLink();
      const url = res?.data?.data?.onboarding_url;
      if (!res?.data?.status || !url) throw new Error(res?.data?.message);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      toast.error(apiErrorMessage(err, err?.message || "Unable to start Stripe onboarding."));
    } finally {
      setLinking(false);
    }
  };

  if (loading) return <Spinner />;

  const connected = !!status?.connected;
  const active = status?.stripe_account_status === "active";
  const requirements = Array.isArray(status?.requirements_due) ? status.requirements_due : [];

  return (
    <div className="lwp-card lwp-payout-card">
      <div className="lwp-stripe-head">
        <StripeLogo />
        <strong>{connected ? "Stripe Account" : "Connect Stripe Account"}</strong>
      </div>
      <p className="lwp-muted lwp-small">Connect your Stripe account to receive automatic payment splits.</p>

      {connected && (
        <span className={`lwp-status ${active ? "lwp-status-ended" : "lwp-status-pending"} lwp-stripe-badge`}>
          {active ? "Active" : "Needs Verification"}
        </span>
      )}

      {connected && active && (
        <p className="lwp-muted lwp-small lwp-stripe-flags">
          Payouts {status.payouts_enabled ? "enabled" : "pending"} · Charges {status.charges_enabled ? "enabled" : "pending"}
        </p>
      )}

      {requirements.length > 0 && (
        <ul className="lwp-requirements">
          {requirements.map((r) => (
            <li key={r}>{humanize(r)}</li>
          ))}
        </ul>
      )}

      <div className="lwp-payout-actions">
        {(!connected || !active) && (
          <button type="button" className="lwp-btn lwp-btn-stripe" onClick={openOnboarding} disabled={linking}>
            {linking ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <span className="lwp-s-badge">S</span> {connected ? "Complete Verification" : "Connect with Stripe"}
              </>
            )}
          </button>
        )}
        {connected && (
          <a className="lwp-btn lwp-btn-black" href="https://dashboard.stripe.com/" target="_blank" rel="noreferrer">
            <span className="lwp-s-badge">S</span> Open Stripe Dashboard
          </a>
        )}
        {connected && active && isPaypal(preference) && (
          <button type="button" className="lwp-btn lwp-btn-outline lwp-btn-block" onClick={onUseStripe} disabled={savingPref}>
            {savingPref ? <span className="spinner-border spinner-border-sm" /> : "Use Stripe for payouts"}
          </button>
        )}
      </div>
    </div>
  );
};

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

const PaypalPanel = ({ bank, onSaved }) => {
  const [email, setEmail] = useState(bank?.paypal || "");
  const [saving, setSaving] = useState(false);
  // Set once a code has been sent: the email it went to.
  const [otpEmail, setOtpEmail] = useState(null);
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  useEffect(() => setEmail(bank?.paypal || ""), [bank?.paypal]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (otpEmail) inputs.current[0]?.focus();
  }, [otpEmail]);

  const current = (bank?.paypal || "").trim().toLowerCase();
  const isCurrent = isPaypal(bank?.preference) && !!current;

  const sendCode = async (value) => {
    setSaving(true);
    try {
      const res = await lawyerService.sendPaypalOtp(value);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || `OTP sent to ${value}`);
      setOtpEmail(value);
      setDigits(Array(OTP_LENGTH).fill(""));
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to send verification code."));
    } finally {
      setSaving(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast.error("Please enter a valid PayPal email.");
      return;
    }
    // Already verified: only switching the payout method back to PayPal.
    if (value === current) {
      if (isCurrent) return toast.info("This PayPal email is already your payout method.");
      setSaving(true);
      try {
        const res = await lawyerService.updatePayout({ preference: "Paypal" });
        if (!res?.data?.status) throw new Error(res?.data?.message);
        toast.success("PayPal is now your payout method");
        onSaved(res.data.data);
      } catch (err) {
        toast.error(apiErrorMessage(err, err?.message || "Unable to update payout method."));
      } finally {
        setSaving(false);
      }
      return;
    }
    sendCode(value);
  };

  const focusAt = (i) => inputs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))]?.focus();

  const fillFrom = (index, value) => {
    const chars = value.replace(/\D/g, "").slice(0, OTP_LENGTH - index).split("");
    if (!chars.length) return;
    const next = [...digits];
    chars.forEach((c, k) => {
      next[index + k] = c;
    });
    setDigits(next);
    focusAt(index + chars.length);
  };

  const handleChange = (index, value) => {
    if (value === "") {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    fillFrom(index, value);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
      focusAt(index - 1);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length !== OTP_LENGTH) {
      toast.error("Please enter the 4-digit code.");
      return;
    }
    setSaving(true);
    try {
      const res = await lawyerService.verifyPaypalOtp(otpEmail, otp);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "PayPal email verified");
      setOtpEmail(null);
      onSaved(res.data.data?.bank_details || { paypal: otpEmail, preference: "Paypal" });
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Invalid OTP."));
    } finally {
      setSaving(false);
    }
  };

  if (otpEmail) {
    return (
      <form className="lwp-card lwp-payout-card" onSubmit={verify}>
        <strong className="lwp-payout-title">Verify your PayPal email</strong>
        <p className="lwp-muted lwp-small lwp-paypal-otp-sub">
          Enter the 4-digit code we sent to <strong>{otpEmail}</strong>. It expires in 10 minutes.
        </p>
        <div className="lwp-otp-boxes lwp-paypal-otp-boxes">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              className="lwp-otp-box"
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={OTP_LENGTH}
              value={d}
              aria-label={`Digit ${i + 1}`}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => {
                e.preventDefault();
                fillFrom(i, e.clipboardData.getData("text"));
              }}
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>
        <button type="submit" className="lwp-btn" disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : "Verify"}
        </button>
        <div className="lwp-paypal-otp-links">
          <button type="button" className="lwp-link-btn" onClick={() => setOtpEmail(null)} disabled={saving}>
            Change email
          </button>
          <button
            type="button"
            className="lwp-link-btn"
            onClick={() => sendCode(otpEmail)}
            disabled={saving || cooldown > 0}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="lwp-card lwp-payout-card" onSubmit={submitEmail}>
      <strong className="lwp-payout-title">PayPal (Fast and smooth for transfer)</strong>
      {isCurrent && <span className="lwp-status lwp-status-ended lwp-stripe-badge">Current payout method</span>}
      <label className="lwp-paypal-field">
        <input
          type="email"
          className="lwp-input lwp-input-box"
          placeholder="example@gmail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          aria-label="PayPal email"
        />
        <i className="bi bi-paypal" aria-hidden="true" />
      </label>
      <p className="lwp-muted lwp-small">We&apos;ll email a verification code to confirm this PayPal address.</p>
      <button type="submit" className="lwp-btn" disabled={saving}>
        {saving ? <span className="spinner-border spinner-border-sm" /> : email.trim().toLowerCase() === current ? "Use PayPal" : "Send verification code"}
      </button>
    </form>
  );
};

const LawyerPayout = () => {
  const { profile, setProfile, refreshProfile } = useOutletContext();
  const bank = profile?.bank_details || null;
  const [tab, setTab] = useState(null);
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  // Default to the method currently in use once the profile is known.
  useEffect(() => {
    if (tab === null && profile?.id) setTab(isPaypal(bank?.preference) ? "paypal" : "stripe");
  }, [tab, profile?.id, bank?.preference]);

  const saved = (nextBank) => setProfile((p) => ({ ...p, bank_details: { ...(p?.bank_details || {}), ...nextBank } }));

  const useStripe = async () => {
    setSavingPref(true);
    try {
      const res = await lawyerService.updatePayout({ preference: "bank" });
      if (!res?.data?.status) throw new Error(res?.data?.message);
      saved(res.data.data);
      toast.success("Stripe is now your payout method");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update payout method."));
    } finally {
      setSavingPref(false);
    }
  };

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Payout Settings | Legal Platform" noindex />
      <PageHeader title="Payout Settings" backTo="/lawyer/settings" />
      <p className="lwp-payout-lead">Choose how you want to get paid</p>
      {tab === null ? (
        <Spinner />
      ) : (
        <>
          <SegmentTabs
            label="Payout method"
            value={tab}
            onChange={setTab}
            tabs={[
              { key: "stripe", label: "Stripe Connect" },
              { key: "paypal", label: "PayPal" },
            ]}
          />
          <div className="lwp-tab-body">
            {tab === "stripe" ? (
              <StripePanel preference={bank?.preference} onUseStripe={useStripe} savingPref={savingPref} />
            ) : (
              <PaypalPanel bank={bank} onSaved={saved} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LawyerPayout;
